import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Pencil, CheckCircle2, Check, Download, ChevronDown, MoreHorizontal, UserPlus } from 'lucide-react';
import { Spinner } from '../ui';
import { ConnectedNotificationBell } from '../shell/ConnectedNotificationBell';

/**
 * v2 wizard header — the 52px in-flow navy bar every wizard step lives under,
 * ported from the `RFQ Wizard v2*.dc.html` mocks: back arrow, "Project /
 * <name>" with inline rename, saved indicator, Export menu, and Share.
 * Presentational — the wizard page owns navigation and persistence.
 *
 * There is no Continue here. Each step carries its own at the bottom of the
 * work, which is the one people actually use; a second copy in the header did
 * the same thing from a place nobody looked and had to be kept in step with
 * the bottom one. Share takes the primary slot, and only an owner sees it —
 * an editor may change everything on the RFQ except who else can.
 */
export interface WizardExportItem {
  id: string;
  label: string;
  onSelect: () => void;
  busy?: boolean;
}

export interface WizardHeaderProps {
  /** Project name shown after the "Project /" crumb. */
  name: string;
  onBack?: () => void;
  /** Enables the pencil → inline rename; called with the trimmed new name. */
  onRename?: (name: string) => void;
  /** e.g. "Saved just now"; omit to hide the indicator. */
  savedLabel?: string;
  /** Export dropdown entries; omit to hide the Export button. */
  exportItems?: WizardExportItem[];
  /** Opens the Share modal. Omit for anyone who is not the owner — the button
   *  then does not render at all, rather than rendering dead. */
  onShare?: () => void;
  /**
   * "Give feedback" (see `FeedbackButton`); omitted = no entry here. A slot for
   * the same reason the top bar uses one — this header stays presentational and
   * knows nothing about the panel it would open.
   */
  feedbackSlot?: React.ReactNode;
  /**
   * The notification bell. Omitted = the connected bell, so research results
   * reach a buyer who stayed in the wizard; `null` hides it (design gallery).
   */
  notificationsSlot?: React.ReactNode;
}

const RenameInput: React.FC<{ initial: string; onDone: (value: string) => void }> = ({ initial, onDone }) => {
  const [value, setValue] = useState(initial);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => inputRef.current?.select(), []);
  const commit = () => onDone(value.trim() || initial);
  return (
    <span className="flex min-w-0 flex-1 items-center gap-[5px]">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') onDone(initial);
        }}
        aria-label="Project name"
        className="w-[220px] rounded-control border border-chrome-border bg-white/[0.08] px-s3 py-[3px] font-sans text-[14px] font-semibold text-white outline-none focus:border-action max-md:w-full max-md:min-w-0 max-md:text-[16px]"
      />
      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={commit} aria-label="Save name" className="text-[#3BAF7E] hover:text-white">
        <Check className="h-[14px] w-[14px]" />
      </button>
    </span>
  );
};

const ExportMenu: React.FC<{ items: WizardExportItem[] }> = ({ items }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      {open && <button type="button" aria-hidden="true" tabIndex={-1} className="fixed inset-0 z-40 cursor-default" onClick={() => setOpen(false)} />}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-[7px] whitespace-nowrap rounded-control border border-chrome-border px-[12px] py-[7px] text-mono-data font-medium text-[#C6D2E2] hover:bg-white/[0.08]"
      >
        <Download className="h-[13px] w-[13px] text-chrome-text" />
        Export
        <ChevronDown className="h-[13px] w-[13px] text-chrome-text" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-[calc(100%+6px)] z-50 w-[210px] rounded-card border border-line bg-surface p-s2 shadow-[0_12px_32px_-12px_rgba(16,24,40,0.24)]">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              disabled={item.busy}
              onClick={() => {
                item.onSelect();
                setOpen(false);
              }}
              className="flex w-full items-center justify-between rounded-control px-s4 py-s3 text-left text-body-sm font-medium text-ink hover:bg-canvas disabled:opacity-50"
            >
              {item.label}
              {item.busy && <Spinner />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Export and Share on a phone, behind one control.
 *
 * Both are real actions and neither is the work: at 375px the two of them
 * would take half the header and leave the project's own name, which is the
 * one thing this bar exists to say, with nowhere to go. They keep their labels
 * and their order — the same two things, one tap further away.
 */
const MoreMenu: React.FC<{ items: WizardExportItem[]; onShare?: () => void }> = ({ items, onShare }) => {
  const [open, setOpen] = useState(false);
  if (items.length === 0 && !onShare) return null;
  return (
    <div className="relative md:hidden">
      {open && <button type="button" aria-hidden="true" tabIndex={-1} className="fixed inset-0 z-40 cursor-default" onClick={() => setOpen(false)} />}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        className="flex h-9 w-9 items-center justify-center rounded-control border border-chrome-border text-chrome-text hover:bg-white/[0.08]"
      >
        <MoreHorizontal className="h-[17px] w-[17px]" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-[calc(100%+6px)] z-50 w-[220px] rounded-card border border-line bg-surface p-s2 shadow-[0_12px_32px_-12px_rgba(16,24,40,0.24)]">
          {onShare && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onShare(); }}
              className="flex w-full items-center gap-s3 rounded-control px-s4 py-s4 text-left text-body-md font-semibold text-action hover:bg-canvas"
            >
              <UserPlus className="h-[14px] w-[14px]" />
              Share
            </button>
          )}
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              disabled={item.busy}
              onClick={() => { item.onSelect(); setOpen(false); }}
              className="flex w-full items-center justify-between gap-s3 rounded-control px-s4 py-s4 text-left text-body-md font-medium text-ink hover:bg-canvas disabled:opacity-50"
            >
              <span className="flex items-center gap-s3">
                <Download className="h-[14px] w-[14px] text-ink-muted" />
                {item.label}
              </span>
              {item.busy && <Spinner />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const WizardHeader: React.FC<WizardHeaderProps> = ({
  name,
  onBack,
  onRename,
  savedLabel,
  exportItems,
  onShare,
  feedbackSlot,
  notificationsSlot,
}) => {
  const [editing, setEditing] = useState(false);
  return (
    <header className="relative z-10 flex h-header shrink-0 items-center justify-between bg-chrome px-[16px] max-md:px-s5">
      <div className="flex min-w-0 items-center gap-s4">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-control border border-chrome-border text-chrome-text hover:bg-white/[0.08]"
        >
          <ArrowLeft className="h-[15px] w-[15px]" />
        </button>
        <div className="flex min-w-0 items-center gap-[7px] text-[14px]">
          <span className="shrink-0 font-medium text-ink-muted max-md:hidden">Project /</span>
          {editing && onRename ? (
            <RenameInput
              initial={name}
              onDone={(value) => {
                setEditing(false);
                if (value !== name) onRename(value);
              }}
            />
          ) : (
            <>
              <span className="overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-white">{name}</span>
              {onRename && (
                <button type="button" onClick={() => setEditing(true)} aria-label="Rename project" className="shrink-0 text-ink-muted hover:text-white">
                  <Pencil className="h-[13px] w-[13px]" />
                </button>
              )}
            </>
          )}
        </div>
        {savedLabel && (
          <div className="ml-s2 flex shrink-0 items-center gap-[5px] text-meta font-medium text-chrome-text max-md:hidden">
            <CheckCircle2 className="h-[13px] w-[13px] text-[#3BAF7E]" />
            <span className="whitespace-nowrap">{savedLabel}</span>
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-s3">
        {feedbackSlot}
        {notificationsSlot === undefined ? <ConnectedNotificationBell onDark /> : notificationsSlot}
        {exportItems && exportItems.length > 0 && (
          <span className="max-md:hidden">
            <ExportMenu items={exportItems} />
          </span>
        )}
        <MoreMenu items={exportItems ?? []} onShare={onShare} />
        {onShare && (
          <button
            type="button"
            onClick={onShare}
            className="flex items-center gap-[5px] whitespace-nowrap rounded-control bg-action px-[16px] py-[7px] text-body-md font-semibold text-white hover:bg-action-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 max-md:hidden"
          >
            <UserPlus className="h-[13px] w-[13px]" />
            Share
          </button>
        )}
      </div>
    </header>
  );
};

export default WizardHeader;
